import {
  AGE_GROUPS,
  GENDERS,
  LEGACY_SLUG_ALIASES,
  WELLNESS_HUB_SLUG,
  dietSlug,
  getSegmentProfile,
  trainingSlug,
} from './segments';
import { GLOBAL_WELLNESS_HUB_SLUG } from './global/segments';
import {
  activeDayEatingTable,
  NONVEG_WEEK_DAYS,
  VEG_WEEK_DAYS,
} from './sharedMeals';

const DIET_HERO_VEG =
  'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=1600&q=80';
const DIET_HERO_NONVEG =
  'https://images.unsplash.com/photo-1603360946369-dc9bb6258143?auto=format&fit=crop&w=1600&q=80';
const EXERCISE_HERO =
  'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=1600&q=80';
const HUB_HERO =
  'https://images.unsplash.com/photo-1552674605-db6ffd4facb5?auto=format&fit=crop&w=1600&q=80';

const BASE_PUBLISHED = new Date('2026-07-21T12:00:00.000Z').getTime();

function publishedOffset(index) {
  return new Date(BASE_PUBLISHED + index * 60_000).toISOString();
}

function buildDietPost(dietType, genderKey, ageKey, index) {
  const isVeg = dietType === 'vegetarian';
  const profile = getSegmentProfile(genderKey, ageKey);
  const audience = profile.audience;
  const slug = dietSlug(dietType, genderKey, ageKey);
  const otherDietSlug = dietSlug(isVeg ? 'non-vegetarian' : 'vegetarian', genderKey, ageKey);
  const training = trainingSlug(genderKey, ageKey);

  const dietLabel = isVeg ? 'Vegetarian (eggs OK)' : 'Non-vegetarian';
  const dietTitle = `7-Day ${isVeg ? 'Vegetarian' : 'Non-Vegetarian'} Healthy Diet for ${audience} (Indian in Ohio)`;

  return {
    id: `wellness-diet-${dietType}-${genderKey}-${ageKey}`,
    slug,
    title: dietTitle,
    excerpt: `${dietLabel} Indian-style healthy meal plan for ${audience.toLowerCase()} in Ohio—balanced protein, fiber, and home-cooked meals. Not medical advice.`,
    publishedAt: publishedOffset(index),
    coverImageUrl: isVeg ? DIET_HERO_VEG : DIET_HERO_NONVEG,
    authorName: 'Daily Sports Life',
    clubName: 'Player Wellness',
    audience,
    featured: true,
    component: 'WellnessGuideBlogArticle',
    theme: 'diet',
    blogCategory: 'food-diet',
    wellnessMeta: {
      planType: isVeg ? 'vegetarian-diet' : 'nonveg-diet',
      gender: genderKey,
      ageKey,
    },
    companionPost: {
      slug: training,
      title: profile.exerciseTitle,
      blurb:
        genderKey === 'women'
          ? 'general fitness and core matched to this age group.'
          : 'strength and conditioning matched to this age group.',
    },
    relatedPosts: [
      {
        slug: otherDietSlug,
        title: `7-Day ${isVeg ? 'Non-Vegetarian' : 'Vegetarian'} Healthy Diet for ${audience} (Indian in Ohio)`,
        blurb: isVeg ? 'adds chicken and fish with the same structure.' : 'eggs and dal if you skip meat.',
      },
      {
        slug: WELLNESS_HUB_SLUG,
        title: 'Ohio Indian Health & Wellness Guide (All Ages)',
        blurb: 'pick another age or gender plan.',
      },
    ],
    sections: [
      {
        type: 'stats',
        ariaLabel: 'Plan at a glance',
        items: [
          { value: '7 days', label: 'Meal rotation' },
          { value: profile.proteinRange, label: 'Protein guide' },
          { value: audience, label: 'Written for' },
          { value: isVeg ? 'Eggs + dal' : 'Chicken + fish', label: 'Anchors' },
        ],
      },
      {
        type: 'panel',
        title: 'Who this plan is for',
        paragraphs: [
          `${profile.dietWhoLead} You eat ${isVeg ? 'vegetarian food with eggs' : 'meat, fish, and eggs'} and shop Indian grocers plus Ohio supermarkets.`,
          'This is a general healthy-eating guide—not medical advice. Your doctor or dietitian should personalize portions if you manage diabetes, kidney disease, pregnancy, or heart conditions.',
        ],
        chips: [audience, dietLabel, 'Ohio Indian', '7-day menu', 'Batch prep friendly'],
      },
      {
        type: 'panel',
        title: `Nutrition priorities for ${audience.toLowerCase()}`,
        list: profile.nutritionPriorities,
        callout:
          'If you take prescription medications or have food allergies, confirm this plan with a licensed clinician before changing how you eat.',
      },
      {
        type: 'panel',
        title: 'Step 1 — Daily eating template',
        orderedList: [
          'Breakfast: protein + complex carb + fruit.',
          'Mid-morning: fruit, nuts, or yogurt if lunch is later.',
          'Lunch: dal/legumes or lean protein + rice or roti + sabzi.',
          'Afternoon snack: fruit, yogurt, or a small handful of nuts.',
          'Dinner: vegetables + protein + moderate starch.',
          'Hydration: water through the day; extra in hot Ohio summers.',
        ],
        callout: `Many adults in this group aim for about ${profile.proteinRange} of body weight per day in protein unless your clinician says otherwise. Spread protein across meals.`,
      },
      {
        type: 'panel',
        title: 'Step 2 — Ohio shopping list',
        list: isVeg
          ? [
              'Indian groceries: dal, rice, atta, paneer, spices, frozen methi, coconut.',
              'Supermarkets: eggs, Greek yogurt, tofu, oats, frozen vegetables, berries, bananas.',
              'Optional: whey protein for quick snacks or smoothies.',
            ]
          : [
              'Proteins: skinless chicken, salmon/tilapia, eggs, Greek yogurt.',
              'Indian groceries: dal, rice, atta, spices, ginger-garlic, ghee.',
              'Produce & pantry: spinach, broccoli, olive oil, oats, chickpeas.',
            ],
      },
      {
        type: 'panel',
        title: 'Step 3 — Sunday batch prep (60–90 min)',
        orderedList: isVeg
          ? [
              'Cook dal and one legume dish (chana or rajma).',
              'Cook rice or millet in bulk.',
              'Hard-boil eggs; marinate paneer or tofu.',
              'Chop vegetables for the week.',
            ]
          : [
              'Marinate and bake/grill chicken batches.',
              'Cook dal; portion fish for 2–3 days.',
              'Hard-boil eggs; cook rice in bulk.',
              'Chop vegetables for stir-fries and salads.',
            ],
      },
      {
        type: 'dayBlocks',
        title: 'Step 4 — Your 7-day menu',
        intro: profile.portionNote,
        days: isVeg ? VEG_WEEK_DAYS : NONVEG_WEEK_DAYS,
      },
      activeDayEatingTable(isVeg),
      {
        type: 'panel',
        title: 'Nutrients to watch in Ohio',
        list: isVeg
          ? [
              'B12 and iron: eggs, dairy, dal; confirm with labs.',
              'Vitamin D in winter—test and supplement per your doctor.',
              'Omega-3: walnuts, flax, chia; algae DHA if no fish.',
            ]
          : [
              'Iron with vitamin C from lemon and vegetables.',
              'Vitamin D in winter—test and supplement per your doctor.',
              'Omega-3: fish twice weekly when possible.',
              'Sodium from restaurant food—cook at home when you can.',
            ],
      },
      {
        type: 'panel',
        title: 'Step 5 — Track what matters',
        paragraphs: [
          'Weekly weigh-in (same time), energy through the day, and how clothes fit—not daily scale noise.',
          'Aim for 80% home cooking. Favor tandoori, dal, and roti at Indian restaurants; share heavy creamy dishes.',
        ],
      },
    ],
  };
}

function buildWomenTrainingPost(genderKey, ageKey, index, profile, audience, slug, vegSlug) {
  const squatLine = profile.jointEmphasis
    ? 'Goblet squat or leg press — 3 × 10–12 (pain-free range)'
    : 'Goblet squat — 3 × 10–12';

  const hiitNote = profile.hiitRecommended
    ? 'Optional: 4 × 20 sec brisk effort on bike or walk, with full recovery—skip if tired.'
    : 'Mobility and walking only—add intervals only if your physician clears higher intensity.';

  return {
    id: `wellness-training-${genderKey}-${ageKey}`,
    slug,
    title: profile.exerciseTitle,
    excerpt: `${profile.exerciseFocus} for ${audience.toLowerCase()}—home or gym friendly, three days per week plus walking. Not medical advice.`,
    publishedAt: publishedOffset(index),
    coverImageUrl: EXERCISE_HERO,
    authorName: 'Daily Sports Life',
    clubName: 'Player Wellness',
    audience,
    featured: true,
    component: 'WellnessGuideBlogArticle',
    theme: 'exercise',
    blogCategory: 'exercise',
    wellnessMeta: { planType: 'training', gender: genderKey, ageKey },
    companionPost: {
      slug: vegSlug,
      title: `7-Day Vegetarian Healthy Diet for ${audience} (Indian in Ohio)`,
      blurb: 'balanced meals matched to this fitness plan (non-veg plan also available).',
    },
    relatedPosts: [
      {
        slug: dietSlug('non-vegetarian', genderKey, ageKey),
        title: `7-Day Non-Vegetarian Healthy Diet for ${audience} (Indian in Ohio)`,
        blurb: 'companion meal plan with chicken and fish.',
      },
      {
        slug: WELLNESS_HUB_SLUG,
        title: 'Ohio Indian Health & Wellness Guide (All Ages)',
        blurb: 'browse all meal and fitness plans by age.',
      },
    ],
    sections: [
      {
        type: 'stats',
        ariaLabel: 'Program overview',
        items: [
          { value: audience, label: 'Written for' },
          { value: '3×', label: 'Strength / week' },
          { value: '2×', label: 'Walking / cardio' },
          { value: '1×', label: 'Mobility' },
        ],
      },
      {
        type: 'panel',
        title: `Built for ${audience.toLowerCase()}`,
        paragraphs: [
          `This program is for ${audience.toLowerCase()} who want stronger muscles, a stable core, and better stamina—without a sport-specific schedule. It fits around work, family, and Ohio winters.`,
          'Get medical clearance before starting if you are pregnant, have heart disease, hypertension, diabetes, joint issues, or have been inactive.',
          'Stop for chest pain, severe breathlessness, dizziness, or sharp joint pain—seek care.',
        ],
        chips: [audience, 'Home or gym', 'Low impact options', 'Ohio'],
      },
      {
        type: 'table',
        title: 'Weekly schedule',
        intro: 'Rest at least one day between hard strength sessions. Swap days if life gets busy—consistency beats perfection.',
        headers: ['Day', 'Session', 'Duration', 'Focus'],
        rows: [
          ['Monday', 'Lower body + core', '40–50 min', 'Legs, glutes, anti-rotation core'],
          ['Tuesday', 'Brisk walk or bike', '30–40 min', 'Easy conversational pace (Zone 2)'],
          ['Wednesday', 'Upper body + core', '40–50 min', 'Push, pull, trunk stability'],
          ['Thursday', 'Rest or gentle yoga', '20–30 min', 'Hips, shoulders, breathing'],
          ['Friday', 'Full-body strength', '40–50 min', 'Compound moves, moderate load'],
          ['Saturday', 'Walk + mobility', '25–40 min', hiitNote],
          ['Sunday', 'Rest or light stretch', '15–20 min', 'Recovery'],
        ],
      },
      {
        type: 'panel',
        title: `Warm-up (${profile.warmupMinutes} minutes)`,
        orderedList: [
          '3 min brisk walk or marching in place',
          'Arm circles and hip circles',
          'Walking lunges (8 per leg)',
          'Band pull-aparts or wall slides (15)',
          'Bodyweight squats to comfortable depth (2 × 10)',
        ],
      },
      {
        type: 'panel',
        title: 'Monday — lower body + core',
        list: [
          squatLine,
          'Glute bridge or hip thrust — 3 × 12',
          'Step-ups (low step) — 3 × 10 each leg',
          'Pallof press — 3 × 12 per side',
          'Dead bug — 3 × 10 per side',
          'Plank — 3 × 20–45 sec',
        ],
      },
      {
        type: 'panel',
        title: 'Wednesday — upper body + core',
        list: [
          'Wall push-up or incline push-up — 3 × 10–15',
          'Dumbbell row or resistance-band row — 3 × 12 per side',
          'Dumbbell overhead press (light) — 3 × 10',
          'Band pull-apart — 3 × 15',
          'Side plank — 3 × 20–40 sec per side',
          'Bird dog — 3 × 8 per side',
        ],
      },
      {
        type: 'panel',
        title: 'Friday — full body',
        list: [
          'Sumo squat or sit-to-stand from chair — 3 × 12',
          'Romanian deadlift (dumbbells) — 3 × 10',
          'Chest press (dumbbells or bands) — 3 × 10',
          'Lat pulldown or band pulldown — 3 × 10',
          'Farmer carry — 3 × 30 sec',
        ],
      },
      {
        type: 'panel',
        title: 'Recovery & women’s health notes',
        list: [
          '7–9 hours sleep; protein spread across the day (see companion diet plans).',
          'Pelvic floor friendly: breathe through lifts; see a pelvic PT if you have pressure or prolapse symptoms.',
          'During pregnancy or postpartum, use only plans approved by your OB or pelvic specialist.',
          'Foam roll or gentle stretch on Tuesday and Friday if it feels good.',
        ],
      },
    ],
  };
}

function buildTrainingPost(genderKey, ageKey, index) {
  const profile = getSegmentProfile(genderKey, ageKey);
  const audience = profile.audience;
  const slug = trainingSlug(genderKey, ageKey);
  const vegSlug = dietSlug('vegetarian', genderKey, ageKey);

  if (genderKey === 'women') {
    return buildWomenTrainingPost(genderKey, ageKey, index, profile, audience, slug, vegSlug);
  }

  const hiitNote = profile.hiitRecommended
    ? 'Optional HIIT only when fully recovered for Sunday 7:00 AM.'
    : 'Default: mobility only—skip HIIT unless your physician clears high-intensity work.';

  const squatLine = profile.jointEmphasis
    ? 'Goblet squat or leg press — 4 × 8–10 (pain-free range)'
    : 'Goblet or back squat — 4 × 6–10';

  return {
    id: `wellness-training-${genderKey}-${ageKey}`,
    slug,
    title: profile.exerciseTitle,
    excerpt: `${profile.exerciseFocus} for ${audience.toLowerCase()} around Sunday 7:00 AM, variable Monday, and Thursday 7:00 PM soccer in Ohio. Not medical advice.`,
    publishedAt: publishedOffset(index),
    coverImageUrl: EXERCISE_HERO,
    authorName: 'Daily Sports Life',
    clubName: 'Player Wellness',
    audience,
    featured: true,
    component: 'WellnessGuideBlogArticle',
    theme: 'exercise',
    blogCategory: 'exercise',
    wellnessMeta: { planType: 'training', gender: genderKey, ageKey },
    companionPost: {
      slug: vegSlug,
      title: `7-Day Vegetarian Healthy Diet for ${audience} (Indian in Ohio)`,
      blurb: 'balanced meals for this age group (non-veg plan also available).',
    },
    relatedPosts: [
      {
        slug: dietSlug('non-vegetarian', genderKey, ageKey),
        title: `7-Day Non-Vegetarian Healthy Diet for ${audience} (Indian in Ohio)`,
        blurb: 'companion meal plan with chicken and fish.',
      },
      {
        slug: WELLNESS_HUB_SLUG,
        title: 'Ohio Indian Health & Wellness Guide (All Ages)',
        blurb: 'all age and gender plans in one place.',
      },
    ],
    referenceLinks: [
      {
        label: 'FIFA 11+ warm-up',
        url: 'https://inside.fifa.com/health-and-medical/injury-prevention',
      },
      {
        label: 'CDC — physical activity for adults',
        url: 'https://www.cdc.gov/physical-activity-basics/guidelines/adults.html',
      },
    ],
    sections: [
      {
        type: 'stats',
        ariaLabel: 'Program overview',
        items: [
          { value: audience, label: 'Written for' },
          { value: '3×', label: 'Soccer / week' },
          { value: '2×', label: 'Strength / week' },
          { value: '2×', label: 'Zone 2 cardio' },
        ],
      },
      {
        type: 'panel',
        title: `Built for ${audience.toLowerCase()} and your club schedule`,
        paragraphs: [
          `This program targets ${audience.toLowerCase()} who play soccer with Indian community teams in Ohio. Practice: Sunday 7:00 AM, Thursday 7:00 PM, Monday at a variable time.`,
          'Get medical clearance before starting if you have heart disease, hypertension, diabetes, joint replacements, or have been inactive.',
          'Stop for chest pain, severe breathlessness, dizziness, or sharp joint pain—seek care.',
        ],
        chips: [audience, 'Sun 7:00 AM', 'Mon — varies', 'Thu 7:00 PM'],
      },
      {
        type: 'table',
        title: 'Weekly schedule',
        intro: 'No heavy lifting on soccer days. Move Tuesday gym to Wednesday after a hard Monday night.',
        headers: ['Day', 'Session', 'Duration', 'Focus'],
        rows: [
          ['Sunday', 'Soccer — 7:00 AM', 'As scheduled', 'Arrive ~6:30 AM; full team warm-up'],
          ['Sunday PM', 'Rest / easy walk', '15–20 min', 'No HIIT'],
          ['Monday', 'Mobility + soccer (time varies)', '15 min + practice', 'No gym before session'],
          ['Tuesday', 'Lower body + core', '45–55 min', 'Squat pattern, hinges, anti-rotation'],
          ['Wednesday', 'Upper body + core', '45–55 min', 'Push, pull, trunk'],
          ['Thursday', 'Activation + soccer — 7:00 PM', '10 min + practice', 'Snack ~5:30 PM; activate ~6:45 PM'],
          ['Friday', 'Zone 2 cardio', '35–45 min', 'Conversational pace'],
          ['Saturday', 'Mobility + optional HIIT', '25–40 min', hiitNote],
        ],
      },
      {
        type: 'panel',
        title: `Warm-up (${profile.warmupMinutes} minutes)`,
        orderedList: [
          '3 min easy bike or brisk walk',
          'Leg swings and hip circles',
          'Walking lunges with rotation (8 per leg)',
          'Band pull-aparts (15)',
          'Bodyweight squats to comfortable depth (2 × 10)',
        ],
      },
      {
        type: 'panel',
        title: 'Tuesday — lower body',
        list: [
          `${squatLine}`,
          'Romanian deadlift (dumbbells or trap bar) — 3 × 8–10',
          'Split squat — 3 × 8 each leg',
          'Calf raises — 3 × 15',
          'Pallof press — 3 × 12 per side',
          'Plank — 3 × 30–60 sec',
        ],
      },
      {
        type: 'panel',
        title: 'Wednesday — upper body + core',
        list: [
          'Push-ups (incline if needed) or dumbbell press — 4 × 8–12',
          'One-arm row — 3 × 10 per side',
          'Dumbbell overhead press — 3 × 8–10',
          'Lat pulldown or assisted pull-up — 3 × 8–12',
          'Dead bug — 3 × 10 per side',
          'Side plank — 3 × 30–45 sec per side',
        ],
      },
      {
        type: 'panel',
        title: 'Recovery',
        list: [
          '7–9 hours sleep; Saturday night critical for Sunday 7:00 AM.',
          'Protein within an hour after each soccer session.',
          'Foam roll quads and glutes on Tuesday and Friday.',
          'Annual physical with your doctor if you have not had one this year.',
        ],
      },
    ],
  };
}

export function buildAllWellnessPosts() {
  const posts = [];
  let index = 1;

  GENDERS.forEach((gender) => {
    AGE_GROUPS.forEach((age) => {
      posts.push(buildDietPost('vegetarian', gender.key, age.key, index++));
      posts.push(buildDietPost('non-vegetarian', gender.key, age.key, index++));
      posts.push(buildTrainingPost(gender.key, age.key, index++));
    });
  });

  return posts;
}

export function buildWellnessHubPost(allPosts) {
  const dietVeg = allPosts.filter((p) => p.wellnessMeta?.planType === 'vegetarian-diet');
  const dietNonVeg = allPosts.filter((p) => p.wellnessMeta?.planType === 'nonveg-diet');
  const trainingMen = allPosts.filter(
    (p) => p.wellnessMeta?.planType === 'training' && p.wellnessMeta?.gender === 'men'
  );
  const trainingWomen = allPosts.filter(
    (p) => p.wellnessMeta?.planType === 'training' && p.wellnessMeta?.gender === 'women'
  );

  const linkGroups = [
    { title: 'Vegetarian 7-day meal plans (eggs OK)', posts: dietVeg },
    { title: 'Non-vegetarian 7-day meal plans', posts: dietNonVeg },
    { title: "Women's fitness & core plans", posts: trainingWomen },
    { title: "Men's soccer training & core plans", posts: trainingMen },
  ];

  return {
    id: 'wellness-hub-ohio-indian',
    slug: WELLNESS_HUB_SLUG,
    title: 'Ohio Indian Health & Wellness Guide (All Ages & Genders)',
    excerpt:
      'Choose a 7-day healthy diet or fitness plan for your age and gender—Indian flavors, Ohio living. Men’s training includes soccer schedules; women’s plans are general fitness. Not medical advice.',
    publishedAt: publishedOffset(0),
    coverImageUrl: HUB_HERO,
    authorName: 'Daily Sports Life',
    clubName: 'Player Wellness',
    audience: 'All adults',
    featured: true,
    component: 'WellnessGuideBlogArticle',
    theme: 'diet',
    blogCategory: 'food-diet',
    wellnessMeta: { planType: 'hub' },
    sections: [
      {
        type: 'panel',
        title: 'How to use this library',
        paragraphs: [
          'Pick the guide that matches your age and gender. Meal plans are general healthy Indian eating (vegetarian with eggs or non-vegetarian). Women’s training plans focus on strength, core, and walking—no sport required. Men’s training plans are built around a soccer practice week.',
          'For universal meal plans without Indian or Ohio-specific ingredients, see the global Healthy Eating & Wellness Guide.',
          'Use local Indian grocers and supermarkets in Ohio for staples. Open the medical & legal disclaimer at the bottom of any plan for full terms. Work with licensed professionals for medical, pregnancy, or injury needs.',
        ],
        chips: ['Women & men', 'Ages 18–34, 35–49, 50+', 'Veg & non-veg', 'Ohio Indian'],
      },
      {
        type: 'hubLinks',
        title: 'All plans',
        groups: linkGroups.map((g) => ({
          title: g.title,
          links: g.posts.map((p) => ({
            slug: p.slug,
            label: p.title,
            meta: p.audience,
          })),
        })),
      },
    ],
    referenceLinks: [
      {
        label: 'Global Healthy Eating & Wellness Guide',
        url: `https://www.dailysportslife.com/blog/${GLOBAL_WELLNESS_HUB_SLUG}`,
      },
    ],
  };
}

export const WELLNESS_GENERATED_POSTS = (() => {
  const generated = buildAllWellnessPosts();
  const hub = buildWellnessHubPost(generated);
  return [hub, ...generated];
})();

export function resolveWellnessSlug(slug) {
  return LEGACY_SLUG_ALIASES[slug] || slug;
}
